import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Calculator } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import ProjectSelect from "@/components/ProjectSelect";
import { fmtTL } from "@/lib/utils";

const EMPTY = { project_id: "", title: "", revision: 0, total_amount: 0, status: "taslak", notes: "" };

export default function Discovery() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get(`/discoveries${filter ? `?project_id=${filter}` : ""}`).then(r => setRows(r.data));
  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)); }, []);
  useEffect(() => { load(); }, [filter]);

  const save = async () => {
    if (!form.project_id || !form.title) { toast.error("Proje ve başlık zorunlu"); return; }
    await api.post("/discoveries", { ...form, revision: Number(form.revision), total_amount: Number(form.total_amount) });
    toast.success("Keşif oluşturuldu"); setOpen(false); setForm(EMPTY); load();
  };
  const remove = async (id) => { await api.delete(`/discoveries/${id}`); toast.success("Silindi"); load(); };

  const statusColor = { taslak: "bg-slate-500/15 text-slate-700 dark:text-slate-300", onaylandi: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", revizyon: "bg-amber-500/15 text-amber-700 dark:text-amber-400" };

  return (
    <div data-testid="discovery-page">
      <PageHeader title="Keşif ve Keşif Revizyonu" subtitle="Metraj ve bütçe keşifleri, revizyonlarıyla birlikte.">
        <ProjectSelect projects={projects} value={filter} onChange={setFilter} testid="filter-project" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-discovery-button" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"><Plus className="w-4 h-4 mr-1" /> Yeni Keşif</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Yeni Keşif</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Proje *</Label><ProjectSelect projects={projects} value={form.project_id} onChange={v => setForm({...form, project_id: v})} includeAll={false} testid="form-project" /></div>
              <div className="col-span-2"><Label>Başlık *</Label><Input data-testid="input-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} /></div>
              <div><Label>Revizyon</Label><Input type="number" value={form.revision} onChange={e => setForm({...form, revision: e.target.value})} /></div>
              <div><Label>Toplam Tutar (TL)</Label><Input type="number" step="0.01" value={form.total_amount} onChange={e => setForm({...form, total_amount: e.target.value})} /></div>
              <div className="col-span-2">
                <Label>Durum</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="taslak">Taslak</SelectItem>
                    <SelectItem value="onaylandi">Onaylandı</SelectItem>
                    <SelectItem value="revizyon">Revizyon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Notlar</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>İptal</Button><Button className="bg-amber-500 hover:bg-amber-600 text-slate-950" onClick={save} data-testid="save-discovery-button">Kaydet</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {rows.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-12 text-center">
          <Calculator className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-semibold">Henüz keşif yok</div>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-muted/40"><TableHead>Başlık</TableHead><TableHead>Rev.</TableHead><TableHead className="text-right">Tutar</TableHead><TableHead>Durum</TableHead><TableHead>Not</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.title}</TableCell>
                  <TableCell className="font-mono-nums">R{r.revision}</TableCell>
                  <TableCell className="text-right font-mono-nums">{fmtTL(r.total_amount)}</TableCell>
                  <TableCell><Badge className={statusColor[r.status]}>{r.status}</Badge></TableCell>
                  <TableCell className="text-muted-foreground truncate max-w-xs">{r.notes || "-"}</TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
